import { z } from "zod";
import {
  accessFields,
  actorSchema,
  contextStatusSchema,
  idSchema,
  instantSchema,
  sourceDateSchema,
} from "@amber/contracts";
import { languageTagSchema, msTupleSchema, scoreSchema, withdrawalCauseSchema } from "./shared.js";

export const EVENT_KINDS = ["interview", "speech", "parliamentary_exchange", "authored_statement"] as const;
export const eventKindSchema = z.enum(EVENT_KINDS);

export const communicationRecordSchema = z.strictObject({
  id: idSchema("com"),
  event_kind: eventKindSchema,
  occurrence_date: sourceDateSchema,
  language: languageTagSchema,
  current_revision_id: idSchema("rev").nullable(),
  primary_copy_id: idSchema("cpy"),
  context_status: contextStatusSchema,
  split_from: z.strictObject({ communication_id: idSchema("com"), split_id: idSchema("spl") }).nullable(),
  merged_into: z.strictObject({ communication_id: idSchema("com"), merge_id: idSchema("spl") }).nullable(),
  first_eligible_at: instantSchema.nullable(),
  timestamps: z.strictObject({
    discovered_at: instantSchema,
    archived_at: instantSchema.nullable(),
    processed_at: instantSchema.nullable(),
    source_available_at: instantSchema.nullable(),
  }),
  ...accessFields,
});
export type CommunicationRecord = z.infer<typeof communicationRecordSchema>;

export const EVIDENCE_REVISION_STATES = ["candidate", "current", "superseded", "withdrawn"] as const;
export const evidenceRevisionStateSchema = z.enum(EVIDENCE_REVISION_STATES);

const unitReplacementSchema = z.union([
  idSchema("unt"),
  z.strictObject({ communication_id: idSchema("com"), unit_id: idSchema("unt") }),
]);

export const evidenceRevisionRecordSchema = z
  .strictObject({
    id: idSchema("rev"),
    communication_id: idSchema("com"),
    version: z.number().int().positive(),
    state: evidenceRevisionStateSchema,
    result_id: idSchema("res").nullable(),
    base_correction_revision: z.number().int().nonnegative(),
    unit_ids: z.array(idSchema("unt")),
    context_status: contextStatusSchema,
    published_at: instantSchema.nullable(),
    superseded_at: instantSchema.nullable(),
    withdrawn: z
      .strictObject({
        at: instantSchema,
        cause: withdrawalCauseSchema,
        notice: z.string().min(1),
        change_position: z.number().int().positive(),
      })
      .nullable(),
    replacement_map: z
      .strictObject({
        cause: z.union([idSchema("cor"), idSchema("res"), idSchema("spl")]),
        units: z.record(idSchema("unt"), unitReplacementSchema),
      })
      .nullable(),
  })
  .refine(
    (r) => (r.state === "candidate") === (r.published_at === null),
    "only a candidate revision lacks published_at",
  )
  .refine(
    (r) => (r.state === "superseded") === (r.superseded_at !== null),
    "superseded_at is set exactly when superseded",
  )
  .refine((r) => (r.state === "withdrawn") === (r.withdrawn !== null), "withdrawn is set exactly when withdrawn");
export type EvidenceRevisionRecord = z.infer<typeof evidenceRevisionRecordSchema>;

export const UNIT_KINDS = ["turn", "written_unit", "interjection", "unintelligible"] as const;
export const unitKindSchema = z.enum(UNIT_KINDS);

export const UNIT_FLAGS = ["overlap", "interjection", "low_confidence", "flagged_problem"] as const;
export const unitFlagSchema = z.enum(UNIT_FLAGS);

export const unitRecordSchema = z.strictObject({
  id: idSchema("unt"),
  introduced_in_revision_id: idSchema("rev"),
  kind: unitKindSchema,
  speaker_id: idSchema("spk").nullable(),
  assignment_id: idSchema("asg").nullable(),
  text: z.string().min(1),
  language: languageTagSchema,
  original_mention: z.string().min(1).nullable(),
  locator: z.strictObject({
    source_version_id: idSchema("ver"),
    range_ms: msTupleSchema.nullable(),
  }),
  word_ids: z.array(z.string().min(1)),
  flags: z.array(unitFlagSchema),
  replaces: idSchema("unt").optional(),
});
export type UnitRecord = z.infer<typeof unitRecordSchema>;

export const COMMUNICATION_RELATION_KINDS = ["split", "merge"] as const;
export const COMMUNICATION_RELATION_STATES = ["active", "reversed"] as const;
export const communicationRelationStateSchema = z.enum(COMMUNICATION_RELATION_STATES);

const relationCommon = {
  id: idSchema("spl"),
  correction_id: idSchema("cor"),
  state: communicationRelationStateSchema,
  created_at: instantSchema,
  reversed: z.strictObject({ at: instantSchema, by: actorSchema, reason: z.string().min(1) }).nullable(),
};

export const communicationRelationRecordSchema = z
  .discriminatedUnion("kind", [
    z.strictObject({
      kind: z.literal("split"),
      source_communication_id: idSchema("com"),
      result_communication_ids: z.array(idSchema("com")).min(2),
      ...relationCommon,
    }),
    z.strictObject({
      kind: z.literal("merge"),
      source_communication_ids: z.array(idSchema("com")).min(2),
      result_communication_id: idSchema("com"),
      ...relationCommon,
    }),
  ])
  .refine(
    (r) => (r.state === "reversed") === (r.reversed !== null),
    "reversed is set exactly when the relation is reversed",
  );
export type CommunicationRelationRecord = z.infer<typeof communicationRelationRecordSchema>;

export const COPY_LINK_STATES = ["proposed", "linked", "rejected", "separated"] as const;
export const copyLinkStateSchema = z.enum(COPY_LINK_STATES);
export const COPY_ROLES = ["primary", "variant"] as const;
export const copyRoleSchema = z.enum(COPY_ROLES);
export const COPY_RELATIONSHIPS = [
  "full_original",
  "edited_edition",
  "extract",
  "preview",
  "syndicated",
  "transcript",
  "translation",
  "dub",
  "synthetic",
] as const;
export const copyRelationshipSchema = z.enum(COPY_RELATIONSHIPS);

export const copyLinkRecordSchema = z
  .strictObject({
    id: idSchema("cpy"),
    communication_id: idSchema("com"),
    item_id: idSchema("itm"),
    range_in_item_ms: msTupleSchema.nullable(),
    relationship: copyRelationshipSchema,
    role: copyRoleSchema,
    state: copyLinkStateSchema,
    decided_by: actorSchema,
    reason: z.string().min(1),
    task_id: idSchema("tsk").optional(),
    created_at: instantSchema,
  })
  .refine((r) => r.state !== "proposed" || r.task_id !== undefined, "a proposed copy link has a task")
  .refine((r) => r.state !== "linked" || r.range_in_item_ms !== null, "a linked copy names its range in the item")
  .refine(
    (r) => r.role !== "primary" || !["translation", "dub", "synthetic"].includes(r.relationship),
    "a translation, dub or synthetic copy is never primary",
  );
export type CopyLinkRecord = z.infer<typeof copyLinkRecordSchema>;

export const ALIGNMENT_CONFIDENCES = ["confident", "uncertain"] as const;
export const alignmentConfidenceSchema = z.enum(ALIGNMENT_CONFIDENCES);

export const alignmentRangeRecordSchema = z.strictObject({
  id: idSchema("aln"),
  from_copy_id: idSchema("cpy"),
  to_copy_id: idSchema("cpy"),
  ranges: z
    .array(
      z.strictObject({
        from_ms: msTupleSchema,
        to_ms: msTupleSchema,
        confidence: alignmentConfidenceSchema,
        score: scoreSchema.nullable(),
      }),
    )
    .min(1),
  method: z.string().min(1),
  created_at: instantSchema,
});
export type AlignmentRangeRecord = z.infer<typeof alignmentRangeRecordSchema>;
